import { Divider, Group, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
	createFacetedRowModel,
	createFacetedUniqueValues,
	createFilteredRowModel,
	createSortedRowModel,
	useTable,
} from '@tanstack/react-table';
import camelCase from 'lodash-es/camelCase';
import { useCallback, useMemo } from 'react';

import { AddIcon, DownloadIcon, ExportIcon, Upload } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useCurrentLocation } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { ToolbarButton } from '@apple/ui/data-table/controls/Toolbar/toolbar-button';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableDownload } from '@apple/ui/data-table/hooks/use-table-download';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { createDataTableColumnHelper } from '@apple/ui/data-table/utils/column-helper';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { Link } from '@apple/ui/link/Link';
import { BulkUploadDialog } from '@apple/utils/files/components/BulkUploadDialog';
import { useFileDownload } from '@apple/utils/files/hooks/useFileDownload';
import { FormattedBoolean } from '@apple/utils/globalization/components/FormattedBoolean';
import { globalizationQueryOptions } from '@apple/utils/globalization/queries';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { marketQueries } from '@apple/features/market/queries';
import {
	getBudgetReportUrl,
	saveBudgets,
	uploadStoreBulkTemplate,
} from '@apple/features/store/api/management';
import { locationTypeSchema } from '@apple/features/store/models';
import type { StoreFilters, StoreOdataListing } from '@apple/features/store/models/listing';
import { getStoreDataTableQueryOptions } from '@apple/features/store/queries/odata';

export const Route = createFileRoute('/_authed/_admin/manage/stores')({
	beforeLoad: args =>
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.CustomerManagement',
		}),
	component: StoreManagementRoute,
});

const defaultFilters: StoreFilters = {};

const features = createDataTableFeatures({
	filteredRowModel: createFilteredRowModel(),
	sortedRowModel: createSortedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedUniqueValues: createFacetedUniqueValues(),
});

function StoreManagementRoute() {
	const { t } = useTranslation('manage');
	const navigate = Route.useNavigate();
	const location = useCurrentLocation();
	const search = Route.useSearch();
	const [uploadOpen, uploadActions] = useDisclosure(false);
	const excelTemplate = useFileDownload({
		method: 'get',
		url: '/api/management/stores/bulk/excel/generate',
		fallbackFilename: 'stores.xlsx',
		errorTitle: t('common:error.title'),
		errorMessage: t('common:error.fileDownload'),
	});
	const columnHelper = createDataTableColumnHelper<StoreOdataListing, StoreFilters>();

	const tableState = useTableState<StoreOdataListing, StoreFilters>({
		search,
		navigate,
		defaultFilters: defaultFilters,
		defaultSorting: [
			{ id: 'locationType', desc: true },
			{ id: 'thirdPartyId', desc: false },
		],
		fieldMap: [],
	});

	const [storesQuery, countriesQuery, markets] = useQueries({
		queries: [
			getStoreDataTableQueryOptions({
				filters: tableState.currentFilterData,
				pagination: tableState.state.pagination,
				commonFilters: {},
				sorting: tableState.state.sorting,
			}),
			globalizationQueryOptions.countries,
			marketQueries.getMarkets(),
		],
	});

	const columns = useMemo(
		() =>
			columnHelper.columns([
				columnHelper.accessor('name', {
					header: t('stores.fields.storeName'),
					filter: {
						group: t('stores.filterGroups.details'),
						label: t('stores.filters.storeName'),
					},
				}),
				columnHelper.accessor('thirdPartyId', {
					header: t('stores.fields.storeId'),
					size: 100,
					filter: {
						group: t('stores.filterGroups.details'),
						label: t('stores.filters.storeId'),
					},
					cell: ({ row }) => (
						<Link
							to='/manage/stores/$storeCode'
							state={{ from: location }}
							params={{
								storeCode: row.original.code,
							}}
						>
							<Text size='sm'>{row.original.thirdPartyId}</Text>
						</Link>
					),
				}),
				columnHelper.filterOnly('customerGroupName', {
					group: t('stores.filterGroups.details'),
					label: t('stores.filters.market'),
					variant: 'select',
					getFilterDisplayValue: value =>
						markets.data?.find(x => x.id === Number(value))?.name ?? String(value),
				}),
				columnHelper.filterOnly('code', {
					group: t('stores.filterGroups.details'),
					label: t('stores.filters.code'),
					variant: 'text',
				}),
				columnHelper.accessor('status', {
					header: t('stores.fields.status'),
					cell: ({ row }) => (
						<Group>
							<FormattedBoolean
								size='sm'
								value={row.original.status === 'Active'}
								useIcon
							/>
							<Text size='sm'>
								{row.original.status === 'Active'
									? t('stores.fields.active')
									: t('stores.fields.inactive')}
							</Text>
						</Group>
					),
					filter: {
						group: t('stores.filterGroups.details'),
						label: t('stores.filters.status'),
						variant: 'segmented-switch',
						getFilterDisplayValue: x => {
							switch (x) {
								case 'Active':
									return t('stores.fields.active');
								case 'Inactive':
									return t('stores.fields.inactive');
								default:
									return t('common:label.all');
							}
						},
					},
				}),
				columnHelper.accessor('costCenter', {
					header: t('stores.fields.costCenter'),
					enableColumnFilter: false,
				}),
				columnHelper.accessor('locationType', {
					header: t('stores.fields.locationType'),
					filter: {
						group: t('stores.filterGroups.details'),
						label: t('stores.fields.locationType'),
						variant: 'select',
						getFilterDisplayValue: value =>
							t(`store:locationType.${camelCase(String(value))}`),
					},
				}),
				columnHelper.accessor('recipientName', {
					header: t('address:labels.recipient1'),
					enableColumnFilter: false,
				}),
				columnHelper.accessor('streetAddress1', {
					header: t('address:labels.addressLine1'),
					filter: {
						group: t('stores.filterGroups.details'),
						label: t('address:labels.addressLine1'),
					},
				}),
				columnHelper.accessor('streetAddress2', {
					header: t('address:labels.addressLine2'),
					filter: {
						group: t('stores.filterGroups.details'),
						label: t('address:labels.addressLine2'),
					},
				}),
				columnHelper.accessor('streetAddress3', {
					id: 'addressLine3',
					header: t('address:labels.addressLine3'),
					enableColumnFilter: false,
				}),
				columnHelper.accessor('stateOrProvince', {
					header: t('address:labels.stateOrProvince'),
					filter: {
						group: t('stores.filterGroups.details'),
						label: t('address:labels.stateOrProvince'),
					},
				}),
				columnHelper.accessor('city', {
					header: t('address:labels.city'),
					filter: {
						group: t('stores.filterGroups.details'),
						label: t('address:labels.city'),
					},
				}),
				columnHelper.accessor('postalCode', {
					header: t('address:labels.postalCode'),
					filter: {
						group: t('stores.filterGroups.details'),
						label: t('address:labels.postalCode'),
					},
				}),
				columnHelper.accessor('countryCode', {
					header: t('address:labels.countryCode'),
					filter: {
						group: t('stores.filterGroups.details'),
						label: t('address:labels.countryCode'),
						variant: 'select',
						getFilterDisplayValue: value =>
							countriesQuery.data?.find(x => x.key === value)?.value ?? String(value),
					},
				}),
			]),
		[countriesQuery.data, markets.data, t, columnHelper, location],
	);

	const table = useTable({
		features,
		data: storesQuery.data.rows,
		rowCount: storesQuery.data.totalRowCount,
		...tableState,
		enableFilters: true,
		enableColumnFilters: true,
		enableSorting: true,
		manualFiltering: true,
		manualSorting: true,
		manualPagination: true,
		meta: {
			getFacetedUniqueValues: columnId => {
				switch (columnId) {
					case 'countryCode':
						return new Map(countriesQuery.data?.map(x => [x.key, 1]));
					case 'customerGroupName':
						return new Map(markets.data?.map(x => [x.name, 1]));
					case 'status':
						return new Map([undefined, 'Active', 'Inactive'].map(x => [x, 1]));
					case 'locationType':
						return new Map(locationTypeSchema.options.map(x => [x, 1]));
					default:
						return undefined;
				}
			},
		},
		columnResizeMode: 'onChange',
		columns: columns,
	});

	const tableDownload = useTableDownload({
		table,
		queryOptionsFn: getStoreDataTableQueryOptions,
	});

	const handleExport = useCallback(() => {
		void tableDownload.download({
			filters: tableState.currentFilterData,
			fileName: 'Stores',
		});
	}, [tableDownload, tableState.currentFilterData]);

	const budgetReportExcelTemplate = useFileDownload({
		method: 'post',
		url: getBudgetReportUrl(),
		fallbackFilename: 'storeBudget.xlsx',
		errorTitle: t('common:error.title'),
		errorMessage: t('common:error.fileDownload'),
	});

	return (
		<TableLayout
			title={t('stores.title')}
			table={table}
			toolbarButtons={[
				<ToolbarButton
					key='download-budget-template'
					tooltip={t('stores.buttons.download.tooltip')}
					icon={DownloadIcon}
					loading={excelTemplate.downloading}
					onClick={() => void excelTemplate.download()}
				/>,
				<ToolbarButton
					key='upload'
					tooltip={t('stores.buttons.upload.tooltip')}
					icon={Upload}
					disabled={uploadOpen}
					onClick={uploadActions.open}
				/>,
				<Divider key='divider-1' orientation='vertical' mx='xs' />,
				<ToolbarButton
					key='export'
					loading={tableDownload.downloading}
					tooltip={t('stores.buttons.export.tooltip')}
					icon={ExportIcon}
					onClick={handleExport}
				/>,
				<Divider key='divider-2' orientation='vertical' mx='xs' />,
				<ToolbarButton
					key='download-budget-report'
					tooltip={t('stores.buttons.downloadBudget.tooltip')}
					icon={DownloadIcon}
					loading={budgetReportExcelTemplate.downloading}
					onClick={() => void budgetReportExcelTemplate.download()}
				/>,
				<ToolbarButton
					key='add'
					tooltip={t('stores.buttons.add.tooltip')}
					icon={AddIcon}
					onClick={() =>
						void navigate({
							to: '/manage/stores/add',
							state: { from: location },
						})
					}
				/>,
			]}
		>
			<DataTable table={table} minWidth='3000px' loading={storesQuery.isFetching} />
			<BulkUploadDialog
				opened={uploadOpen}
				close={uploadActions.close}
				onUpload={uploadStoreBulkTemplate}
				onSave={saveBudgets}
			/>
		</TableLayout>
	);
}
